"use client";

import { useEffect, useRef, useState, type ReactNode } from "react";
import { ArrowLeft, ArrowRight, RotateCcw } from "lucide-react";
import type { TestConfig } from "@/data/tests/types";
import type { PublicTherapistSummary } from "@/actions/getTherapists";
import EmailCaptureCard, {
  type CaptureEmailInput,
  type CaptureEmailResult,
} from "@/components/teste/EmailCaptureCard";
import SoftTherapistCTA from "@/components/teste/SoftTherapistCTA";
import ZenyraCta from "@/components/ui/zenyra-cta";
import { trackEvent, trackMetaStandardEvent } from "@/lib/analytics";

/**
 * One runner for every test on /teste.
 *
 * The three tests used to carry three near-identical clients, each with its
 * own intro card, stepper wiring and hand-picked colour theme per result. The
 * intro card is gone on purpose: someone who searched "test anxietate" has
 * already decided to take it, and a second "Începe testul" screen between the
 * page and the first question was a click that lost people. The first question
 * is now the first thing in the card.
 *
 * The runner reads everything test-specific from the config, so a new test is
 * a config file and a small wrapper, not a copy of this component. Scoring
 * still happens on the server through the submit action; nothing here
 * computes a category.
 *
 * The wrapper MUST be a client component ("use client") that imports the
 * config itself. `TestConfig` carries functions (`scoring.categorize`,
 * `email.subjectTemplate`), and a server page cannot pass an object holding
 * functions to a client component: Next throws at render. The page passes
 * only serialisable data (therapists) to the wrapper. See
 * `app/teste/anxietate/AnxietateRunner.tsx`.
 */

const ADVANCE_MS = 260;

type SubmitResult<C extends string> = {
  ok: boolean;
  score?: number;
  category?: C;
  error?: string;
};

export type TestRunnerProps<C extends string> = {
  config: TestConfig<C>;
  submit: (answers: number[]) => Promise<SubmitResult<C>>;
  capture: (input: CaptureEmailInput) => Promise<CaptureEmailResult>;
  therapists: PublicTherapistSummary[];
  /** Zenyra topic slug and the message she receives, built from the result. */
  zenyra: {
    topic: string;
    prefill: (score: number, category: C) => string;
  };
  emailCopy: { headline: string; body: string; submitLabel: string };
  /** Categories that show the crisis note right under the score. */
  urgentCategories?: C[];
  /**
   * A crisis check tied to one item rather than to the total.
   *
   * PHQ-9 item 9 asks about thoughts of death or self-harm. A 1 there with a
   * total of 3 is a "minimal" score, and must still open the result on the
   * crisis lines. When `answers[itemIndex] >= minValue`, the result starts
   * with the crisis block, before the score, whatever the category. The same
   * question also carries a one-line pointer to 112 while it is being asked.
   */
  crisis?: {
    itemIndex: number;
    minValue: number;
    /** What the person answered, said back to them, above the numbers. */
    lead: string;
  };
};

type Phase = "test" | "result";

export default function TestRunner<C extends string>({
  config,
  submit,
  capture,
  therapists,
  zenyra,
  emailCopy,
  urgentCategories = [],
  crisis,
}: TestRunnerProps<C>) {
  const [phase, setPhase] = useState<Phase>("test");
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState<(number | null)[]>(
    () => Array(config.questions.length).fill(null),
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{ score: number; category: C } | null>(null);
  const [started, setStarted] = useState(false);
  const topRef = useRef<HTMLDivElement | null>(null);
  /* The pending auto-advance. Held so "Înapoi" pressed inside the 260ms window
     cancels it, instead of the old timer yanking the person forward again. */
  const advanceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const total = config.questions.length;
  const question = config.questions[step];
  const isLast = step === total - 1;
  const allAnswered = answers.every((a) => a !== null);

  const cancelAdvance = () => {
    if (advanceRef.current) {
      clearTimeout(advanceRef.current);
      advanceRef.current = null;
    }
  };

  useEffect(() => cancelAdvance, []);

  /* Event names are unchanged from the per-test clients, so the analytics
     funnels built on them keep working. "started" now fires on the first
     answer rather than on an intro click that no longer exists. */
  useEffect(() => {
    if (phase === "result" && result) {
      trackEvent(`test_${config.slug}_completed`, {
        score: result.score,
        category: result.category,
      });
    }
  }, [phase, result, config.slug]);

  const scrollToTop = () =>
    requestAnimationFrame(() =>
      topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }),
    );

  const pick = (value: number) => {
    if (!started) {
      setStarted(true);
      trackEvent(`test_${config.slug}_started`);
      trackMetaStandardEvent("ViewContent", {
        content_name: `Test ${config.slug} Started`,
        content_type: "quiz",
        content_category: config.slug,
      });
    }
    setAnswers((prev) => {
      const next = [...prev];
      next[step] = value;
      return next;
    });
    cancelAdvance();
    if (!isLast) {
      advanceRef.current = setTimeout(() => {
        advanceRef.current = null;
        setStep((s) => Math.min(s + 1, total - 1));
      }, ADVANCE_MS);
    }
  };

  const goBack = () => {
    cancelAdvance();
    setStep((s) => Math.max(0, s - 1));
  };

  /* Without this, stepping back to change one answer meant re-answering every
     later question to get forward again: picking was the only way forward. */
  const goNext = () => {
    cancelAdvance();
    setStep((s) => Math.min(s + 1, total - 1));
  };

  const handleSubmit = async () => {
    if (!allAnswered) return;
    setError(null);
    setSubmitting(true);
    const res = await submit(answers as number[]);
    setSubmitting(false);
    if (!res.ok || res.score === undefined || !res.category) {
      setError(res.error ?? "Nu am putut calcula rezultatul. Încearcă din nou.");
      return;
    }
    setResult({ score: res.score, category: res.category });
    setPhase("result");
    scrollToTop();
  };

  const retake = () => {
    cancelAdvance();
    setAnswers(Array(total).fill(null));
    setStep(0);
    setResult(null);
    setError(null);
    setPhase("test");
    scrollToTop();
  };

  /* Read from the answers actually submitted, not from the category: the
     point is that it fires whatever the total came to. */
  const crisisAnswer = crisis ? answers[crisis.itemIndex] : null;
  const crisisTriggered =
    !!crisis && typeof crisisAnswer === "number" && crisisAnswer >= crisis.minValue;

  return (
    <div ref={topRef} className="scroll-mt-24">
      {phase === "test" ? (
        <QuestionCard
          step={step}
          total={total}
          text={question.text}
          options={config.options}
          selected={answers[step]}
          onPick={pick}
          onBack={goBack}
          onNext={goNext}
          onSubmit={handleSubmit}
          isLast={isLast}
          canSubmit={allAnswered && !submitting}
          submitting={submitting}
          error={error}
          footnote={config.stepperFootnote}
          notice={
            crisis && step === crisis.itemIndex ? (
              <>
                Dacă te gândești acum să îți faci rău, nu termina testul: sună la{" "}
                <a href="tel:112" className="font-semibold text-[#B4492C] underline underline-offset-2">
                  112
                </a>
                , disponibil oricând.
              </>
            ) : undefined
          }
        />
      ) : (
        result && (
          <Result
            config={config}
            score={result.score}
            category={result.category}
            answers={answers as number[]}
            urgent={urgentCategories.includes(result.category)}
            crisisLead={crisisTriggered ? crisis?.lead : undefined}
            onRetake={retake}
            slot={
              <>
                <ZenyraCta
                  topic={zenyra.topic}
                  location={`teste_${config.slug}_result`}
                  variant="card"
                  title="Spune-i Zenyrei ce ți-a ieșit"
                  subtitle="Poți vorbi cu ea despre scor, despre ce înseamnă pentru tine și despre pașii următori."
                  ctaLabel="Vorbește cu Zenyra"
                  prefill={zenyra.prefill(result.score, result.category)}
                />
                <EmailCaptureCard
                  compact
                  score={result.score}
                  category={result.category}
                  testSlug={config.slug}
                  captureAction={capture}
                  accent="#2C969B"
                  headline={emailCopy.headline}
                  body={emailCopy.body}
                  submitLabel={emailCopy.submitLabel}
                />
              </>
            }
            after={
              config.therapistCTA.triggerCategories.includes(result.category) &&
              config.therapistCTA.blocks[result.category] ? (
                <SoftTherapistCTA
                  category={result.category}
                  block={config.therapistCTA.blocks[result.category]}
                  therapists={therapists}
                  testSlug={config.slug}
                  utmMedium={config.therapistCTA.utmMedium}
                  urgent={urgentCategories.includes(result.category)}
                  accent="#DC7050"
                />
              ) : null
            }
          />
        )
      )}
    </div>
  );
}

/* ── The question ──────────────────────────────────────────────────────────
   Exported for tests whose result is not a single score (the Big Five profile
   on /teste/personalitate), so they ask their questions in the same card
   without borrowing the single-score result below. */

export function QuestionCard({
  step,
  total,
  text,
  options,
  selected,
  onPick,
  onBack,
  onNext,
  onSubmit,
  isLast,
  canSubmit,
  submitting,
  error,
  footnote,
  notice,
}: {
  step: number;
  total: number;
  text: string;
  options: { label: string; value: number }[];
  selected: number | null;
  onPick: (value: number) => void;
  onBack: () => void;
  onNext: () => void;
  onSubmit: () => void;
  isLast: boolean;
  canSubmit: boolean;
  submitting: boolean;
  error: string | null;
  footnote?: string;
  /** A line under the options for this question only, e.g. a crisis pointer. */
  notice?: ReactNode;
}) {
  return (
    <div className="rounded-2xl border border-[#264653]/12 bg-white text-left shadow-[0_18px_48px_-28px_rgba(38,70,83,0.28)]">
      <div className="px-6 pt-6 sm:px-9 sm:pt-8">
        <div className="flex items-center gap-4">
          <div
            className="h-1 flex-1 overflow-hidden rounded-full bg-[#264653]/8"
            role="progressbar"
            aria-valuemin={1}
            aria-valuemax={total}
            aria-valuenow={step + 1}
            aria-label="Progresul testului"
          >
            <div
              className="h-full rounded-full bg-[#2C969B] transition-[width] duration-500 ease-out"
              style={{ width: `${((step + 1) / total) * 100}%` }}
            />
          </div>
          <span className="shrink-0 text-[13px] tabular-nums text-[#264653]/60" aria-live="polite">
            {step + 1} din {total}
          </span>
        </div>

        <h2 className="mt-7 min-h-[3.2em] text-balance font-serif text-[clamp(1.35rem,2.6vw,1.75rem)] font-medium leading-[1.25] tracking-[-0.01em] text-[#264653]">
          {text}
        </h2>

        <div className="mt-6 flex flex-col gap-2" role="radiogroup" aria-label={text}>
          {options.map((opt) => {
            const on = selected === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                role="radio"
                aria-checked={on}
                onClick={() => onPick(opt.value)}
                disabled={submitting}
                className={`flex w-full items-center justify-between gap-3 rounded-xl border px-5 py-4 text-left text-[15.5px] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2C969B]/50 ${
                  on
                    ? "border-[#2C969B] bg-[#EBF5F5] font-medium text-[#264653]"
                    : "border-[#264653]/12 text-[#264653]/85 hover:border-[#264653]/30 hover:bg-[#FFF9F5]"
                }`}
              >
                {opt.label}
                <span
                  aria-hidden
                  className={`flex size-5 shrink-0 items-center justify-center rounded-full border-2 ${
                    on ? "border-[#2C969B] bg-[#2C969B]" : "border-[#264653]/25"
                  }`}
                >
                  {on && <span className="size-2 rounded-full bg-white" />}
                </span>
              </button>
            );
          })}
        </div>

        {notice && (
          <p className="mt-5 rounded-xl border border-[#DC7050]/30 bg-[#DC7050]/6 px-4 py-3 text-[14px] leading-relaxed text-[#264653]">
            {notice}
          </p>
        )}

        {error && (
          <p role="alert" className="mt-5 rounded-xl bg-[#DC7050]/10 px-4 py-3 text-[14px] text-[#B4492C]">
            {error}
          </p>
        )}
      </div>

      <div className="mt-6 flex items-center justify-between gap-3 border-t border-[#264653]/8 px-4 py-3 sm:px-7">
        <button
          type="button"
          onClick={onBack}
          disabled={step === 0 || submitting}
          className="inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-[14px] font-medium text-[#264653]/70 transition-colors hover:text-[#264653] disabled:cursor-not-allowed disabled:text-[#264653]/25"
        >
          <ArrowLeft className="size-4" />
          Înapoi
        </button>
        {isLast ? (
          <button
            type="button"
            onClick={onSubmit}
            disabled={!canSubmit}
            className="inline-flex h-11 items-center gap-2 rounded-lg bg-[#DC7050] px-6 text-[15px] font-semibold text-white transition-colors hover:bg-[#c25c3e] disabled:cursor-not-allowed disabled:bg-[#264653]/12 disabled:text-[#264653]/40"
          >
            {submitting ? "Se calculează..." : "Vezi rezultatul"}
            {!submitting && <ArrowRight className="size-4" />}
          </button>
        ) : (
          <button
            type="button"
            onClick={onNext}
            disabled={selected === null || submitting}
            className="inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-[14px] font-medium text-[#264653]/70 transition-colors hover:text-[#264653] disabled:cursor-default disabled:text-[#264653]/45"
          >
            {selected === null ? "Alege un răspuns" : "Mai departe"}
            {selected !== null && <ArrowRight className="size-4" />}
          </button>
        )}
      </div>

      {footnote && (
        <p className="border-t border-[#264653]/8 px-6 py-3 text-center text-[12.5px] leading-relaxed text-[#264653]/55 sm:px-9">
          {footnote}
        </p>
      )}
    </div>
  );
}

/* ── The result ────────────────────────────────────────────────────────────
   The old result opened on a blurred gradient cover with a 128px number. The
   number is not the point for most people; what it means is. So the score
   sits beside its label on the band scale, where it is read in context, and
   the copy carries the weight. */

function Result<C extends string>({
  config,
  score,
  category,
  answers,
  urgent,
  crisisLead,
  onRetake,
  slot,
  after,
}: {
  config: TestConfig<C>;
  score: number;
  category: C;
  /** The raw answers, so a per-item scale can show which items scored. */
  answers: number[];
  urgent: boolean;
  /** Set when the runner's item-level crisis check fired. */
  crisisLead?: string;
  onRetake: () => void;
  slot: ReactNode;
  after: ReactNode;
}) {
  const copy = config.resultCopy[category];
  const band = config.bands.find((b) => b.category === category);

  return (
    <div className="space-y-4 text-left">
      {crisisLead && <CrisisBlock lead={crisisLead} />}

      <section className="rounded-2xl border border-[#264653]/12 bg-white p-6 sm:p-9">
        <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-[#2C969B]">
          Rezultatul tău
        </p>
        <div className="mt-4 flex flex-wrap items-baseline gap-x-4 gap-y-1">
          <span className="font-serif text-[clamp(3rem,8vw,4.5rem)] font-medium leading-none tabular-nums text-[#264653]">
            {score}
            <span className="ml-1 text-[0.4em] font-sans text-[#264653]/50">
              {config.scoring.scoreSuffix}
            </span>
          </span>
          {band && (
            /* Tinted pill with ink text. The band colours are mid-tones
               (#E0B96C, #7DA86F), and white text on them fell well under
               readable contrast. */
            <span
              className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-[13px] font-semibold text-[#264653]"
              style={{ backgroundColor: `${band.bg}29` }}
            >
              <span aria-hidden className="size-2 rounded-full" style={{ backgroundColor: band.bg }} />
              {band.label}
            </span>
          )}
        </div>
        <h2 className="mt-6 text-balance font-serif text-[clamp(1.5rem,3.2vw,2.1rem)] font-medium leading-[1.15] tracking-[-0.01em] text-[#264653]">
          {copy.headline}
        </h2>
        <p className="mt-3 text-[16.5px] leading-relaxed text-[#264653]/80">{copy.description}</p>

        {urgent && !crisisLead && (
          <div className="mt-6 rounded-xl border border-[#DC7050]/35 bg-[#DC7050]/8 p-4 text-[14.5px] leading-relaxed text-[#264653]">
            <strong className="font-semibold text-[#B4492C]">Dacă ești în pericol acum</strong>, sau
            te gândești să îți faci rău, sună la 112. Nu trebuie să aștepți o programare.
          </div>
        )}

        <BandScale
          config={config}
          score={score}
          category={category}
          answers={answers}
        />
        <p className="mt-5 text-[15px] leading-relaxed text-[#264653]/75">{copy.meaning}</p>
      </section>

      {slot}

      <section className="rounded-2xl border border-[#264653]/12 bg-white p-6 sm:p-9">
        <h3 className="font-serif text-[clamp(1.3rem,2.6vw,1.7rem)] font-medium text-[#264653]">
          Ce poți face acum
        </h3>
        <ol className="mt-4 border-t border-[#264653]/10">
          {copy.actions.map((a, i) => (
            <li key={a} className="flex items-baseline gap-4 border-b border-[#264653]/10 py-4">
              <span className="w-6 shrink-0 font-serif text-[15px] tabular-nums text-[#DC7050]">
                {String(i + 1).padStart(2, "0")}
              </span>
              <span className="text-[15.5px] leading-relaxed text-[#264653]/85">{a}</span>
            </li>
          ))}
        </ol>
      </section>

      {after}

      <div className="flex justify-center pt-1">
        <button
          type="button"
          onClick={onRetake}
          className="inline-flex items-center gap-2 rounded-lg px-4 py-2 text-[14px] font-medium text-[#264653]/65 transition-colors hover:text-[#264653]"
        >
          <RotateCcw className="size-4" />
          Refă testul
        </button>
      </div>
    </div>
  );
}

/* ── The crisis block ──────────────────────────────────────────────────────
   Shown before the score when an item-level crisis check fires, so the
   numbers are the first thing read, not something below a band scale.
   Lines verified 2026-09-13: 112 answers always; TelVerde Antisuicid answers
   ONLY vineri-duminică, 19:00–07:00. Never describe TelVerde as non-stop. */

function CrisisBlock({ lead }: { lead: string }) {
  return (
    <section
      role="alert"
      aria-labelledby="rezultat-criza"
      className="rounded-2xl border-2 border-[#DC7050]/45 bg-[#FDF1EC] p-6 sm:p-9"
    >
      <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-[#B4492C]">
        Înainte de scor
      </p>
      <h2
        id="rezultat-criza"
        className="mt-3 text-balance font-serif text-[clamp(1.4rem,3vw,1.9rem)] font-medium leading-[1.2] text-[#264653]"
      >
        Vorbește azi cu cineva despre gândurile acestea
      </h2>
      <p className="mt-3 text-[16px] leading-relaxed text-[#264653]/85">{lead}</p>

      <ul className="mt-6 space-y-3">
        <li>
          <a
            href="tel:112"
            className="flex items-baseline gap-4 rounded-xl border border-[#DC7050]/35 bg-white px-5 py-4 transition-colors hover:border-[#DC7050]"
          >
            <span className="font-serif text-[28px] font-medium leading-none tabular-nums text-[#B4492C]">
              112
            </span>
            <span className="text-[15px] leading-snug text-[#264653]">
              Dacă ești în pericol acum. Gratuit, disponibil oricând.
            </span>
          </a>
        </li>
        <li>
          <a
            href="tel:0800801200"
            className="flex flex-wrap items-baseline gap-x-4 gap-y-1 rounded-xl border border-[#264653]/12 bg-white px-5 py-4 transition-colors hover:border-[#264653]/35"
          >
            <span className="font-serif text-[20px] font-medium leading-none tabular-nums text-[#264653]">
              0800 801 200
            </span>
            <span className="text-[14.5px] leading-snug text-[#264653]/80">
              TelVerde Antisuicid. Gratuit, dar răspunde doar vineri-duminică,
              între 19:00 și 07:00.
            </span>
          </a>
        </li>
      </ul>

      <p className="mt-5 text-[15px] leading-relaxed text-[#264653]/80">
        Dacă nu ești în pericol imediat, spune azi unei persoane apropiate și
        caută o discuție cu medicul de familie, un psihiatru sau un psihoterapeut.
        Gândurile de felul acesta sunt un simptom, nu o concluzie despre viața ta,
        și se pot trata.
      </p>
    </section>
  );
}

/** "10–14" → [10, 14]; "15" → [15, 15]. Null when the label carries no number. */
function parseRange(range: string): [number, number] | null {
  const nums = range.match(/\d+/g)?.map((n) => parseInt(n, 10));
  if (!nums?.length) return null;
  return [nums[0], nums[1] ?? nums[0]];
}

/**
 * Which items counted as positive, item by item.
 *
 * A binary screener can set its threshold per item: in ASRS Part A the three
 * executive-function items count from „Câteodată” up, the three
 * hyperactivity items only from „Adesea” up. That rule lives in
 * `scoring.perItemRules`, and `options[].positive` is the flat fallback for a
 * screener whose items all share one cutoff.
 */
function positiveItems<C extends string>(
  config: TestConfig<C>,
  answers: number[],
): boolean[] {
  const rules = config.scoring.perItemRules;
  return config.questions.map((_, i) => {
    const answer = answers[i];
    if (answer === undefined) return false;
    const rule = rules?.[i];
    if (rule) return rule.positiveValues.includes(answer);
    return config.options.some((o) => o.value === answer && o.positive === true);
  });
}

/* Where the score sits among the bands. Sum and average scores get a
   continuous scale with a marker; a binary screener counts positive items, so
   it gets one mark per item instead of a scale it does not have.

   That per-item row used to be filled positionally, `i < score`: at 4 din 6 it
   lit the first four marks whatever the person had actually answered, so
   someone who scored on items 2, 3, 5 and 6 was shown a picture of a different
   test. The marks now read the same per-item rules the scoring uses, and the
   row is captioned rather than left as decoration behind `aria-hidden`. */
function BandScale<C extends string>({
  config,
  score,
  category,
  answers,
}: {
  config: TestConfig<C>;
  score: number;
  category: C;
  answers: number[];
}) {
  const band = config.bands.find((b) => b.category === category);

  if (config.scoring.type === "binary") {
    const flags = positiveItems(config, answers);
    /* Scoring is authoritative and happens on the server. If the two ever
       disagree, show the count rather than a contradiction. */
    const perItem = flags.filter(Boolean).length === score ? flags : null;
    const fill = band?.bg ?? "#DC7050";

    return (
      <div className="mt-7">
        <ol className="flex gap-2">
          {config.questions.map((q, i) => {
            const on = perItem ? perItem[i] : i < score;
            return (
              <li
                key={q.id}
                className="h-3 flex-1 rounded-full"
                style={{ backgroundColor: on ? fill : "rgba(38,70,83,0.10)" }}
                title={`Întrebarea ${i + 1}: ${on ? "în zona pozitivă" : "sub prag"}`}
              />
            );
          })}
        </ol>
        <p className="mt-3 text-[14px] leading-relaxed text-[#264653]/70">
          <span className="font-semibold text-[#264653]">
            {score} din {config.questions.length}
          </span>{" "}
          {score === 1 ? "răspuns intră" : "răspunsuri intră"} în zona pozitivă
          {perItem ? (
            <>
              {" "}
              (
              {perItem
                .map((on, i) => (on ? i + 1 : null))
                .filter((n): n is number => n !== null)
                .join(", ")}
              )
            </>
          ) : null}
          . Pragul diferă de la o întrebare la alta.
        </p>
        <ul className="mt-4 grid gap-2 sm:grid-cols-2">
          {config.bands.map((b) => {
            const on = b.category === category;
            return (
              <li
                key={b.category}
                className={`rounded-xl border p-3 text-[13.5px] leading-snug ${
                  on
                    ? "border-[#264653]/30 bg-[#FFF9F5] text-[#264653]"
                    : "border-[#264653]/10 text-[#264653]/55"
                }`}
              >
                <span className={`font-semibold ${on ? "text-[#264653]" : ""}`}>
                  {b.range}
                </span>{" "}
                · {b.label}
              </li>
            );
          })}
        </ul>
      </div>
    );
  }

  const max = config.scoring.scoreMax;
  /* The segments divide the bar into max+1 integer cells (0..max), so the
     marker sits at the centre of its cell. Positioning at score/max put a
     score of 0 on the bar's left edge, with half the marker hanging off it. */
  const pos = Math.min(98, Math.max(2, ((score + 0.5) / (max + 1)) * 100));
  return (
    <div className="mt-7">
      <div className="relative">
        <div className="flex h-3 overflow-hidden rounded-full">
          {config.bands.map((b) => {
            const r = parseRange(b.range);
            const width = r
              ? ((r[1] - r[0] + 1) / (max + 1)) * 100
              : 100 / config.bands.length;
            return (
              <span
                key={b.category}
                style={{ width: `${width}%`, backgroundColor: b.bg }}
                className={b.category === category ? "" : "opacity-35"}
              />
            );
          })}
        </div>
        <span
          aria-hidden
          className="absolute -top-1.5 size-6 -translate-x-1/2 rounded-full border-[3px] border-white bg-[#264653] shadow"
          style={{ left: `${pos}%` }}
        />
      </div>
      <ul className="mt-4 grid gap-x-4 gap-y-2 sm:grid-cols-2 lg:grid-cols-4">
        {config.bands.map((b) => (
          <li
            key={b.category}
            className={`text-[13px] leading-snug ${b.category === category ? "text-[#264653]" : "text-[#264653]/50"}`}
          >
            <span className="font-semibold tabular-nums">{b.range}</span> {b.label}
          </li>
        ))}
      </ul>
    </div>
  );
}
